import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Chip, CircularProgress } from '@mui/material';

import {
  selectProjectResults,
  selectSelectedVersion,
  setSelectedVersion
} from '../../project-details/data-access/project.reducer';

import VersionHistory, { VersionItem } from '../../../../components/common/VersionHistory';
import AbuseSeverityChart from './AbuseSeverityChart';
import AbuseDriftChart from './AbuseDriftChart';
import AbuseIndexChart from './AbuseIndexChart';
import MLAbuseDetectionChart from './MLAbuseDetectionChart';
import api from '../../../../app/api/axios-api';
import { pickLatestCollectionRun } from '../../../../utils/collectionRuns';
import { isWorkbenchNonRerunRun } from '../../../../utils/workbenchRunFilter';

export interface AbuseDataItem {
  Action: string;
  Category: string;
  'Severity Score': number;
  Risk: string;
  Observation: string;
  [key: string]: string | number | boolean | undefined;
}

interface MLAbuseCategoryItem {
  Category: string;
  'Severity Score': number;
  Risk: string;
  Action: string;
  Observation: string;
}

interface MLAbuseDetectionData {
  result_id: string;
  title: string;
  timestamp: string;
  domain: string;
  userId: string;
  projectId: string;
  environment: string;
  ML_Abuse_Detection: MLAbuseCategoryItem[];
  Overall_Abuse_Score: number;
}

interface LLMAbuseAnalysisFinding {
  Project_ID: string;
  Event_Index: number;
  Section: string;
  Category: string;
  Severity_Score: number;
  Severity_Level: string;
  Weight: number;
  Rationale: string;
  Top_Contributors: {
    semantic: number;
    zero_shot: number;
    heur: number;
  };
  Highlighted_Text: string;
  Action: string;
  Timestamp: string;
}

interface LLMAbuseAnalysisSummary {
  Policy_Reference: string;
  Events_Analyzed: number;
  Overall_Abuse_Index?: number;
  OverallAbuseIndex?: number;
  overall_abuse_index?: number;
  Overall_Verdict: string;
}

interface LLMAbuseAnalysis {
  Summary?: LLMAbuseAnalysisSummary;
  Findings?: LLMAbuseAnalysisFinding[];
  Drift?: Array<{
    Event_Index: number;
    Abuse_Score_Percent: number;
  }>;
  Overall_Abuse_Index?: number;
  overall_abuse_index?: number;
  OverallAbuseIndex?: number;
}

export interface AbuseCardRef {
  handleHistoryOpen: () => void;
}

interface NormalizedCustomAbuse {
  ML_ABUSE_DETECTION?: MLAbuseCategoryItem[];
  ML_Abuse_Detection?: MLAbuseCategoryItem[];
  Overall_Abuse_Score?: number;
  LLM_ABUSE_ANALYSIS?: Record<string, unknown> | unknown[];
  llm_abuse_analysis?: Record<string, unknown> | unknown[];
}

interface AbuseCardProps {
  customData?: NormalizedCustomAbuse | Array<Record<string, unknown>>;
  showInlineVersionControls?: boolean;
  lane?: 'ml' | 'llm';
  onVersionsReady?: (info: { total: number; runsWithMl: number; runsWithLlm: number }) => void;
}

interface AbuseDerivedData {
  llmData: AbuseDataItem[];
  mlAbuseDetectionRaw: MLAbuseDetectionData[];
  overallScore: number;
  driftData: Array<{ eventIndex: number; abuseScore: number }>;
}

type AnyRecord = Record<string, any>;

const emptyDerivedData: AbuseDerivedData = {
  llmData: [],
  mlAbuseDetectionRaw: [],
  overallScore: 0,
  driftData: []
};

const hasDerivedAbuseData = (data: AbuseDerivedData): boolean =>
  data.mlAbuseDetectionRaw.length > 0 || data.llmData.length > 0 || data.overallScore > 0 || data.driftData.length > 0;

/** 360-analysis may return abuse_ai as a run array or with mixed ML key casing. */
const normalizeCustomAbuseInput = (input: AbuseCardProps['customData']): NormalizedCustomAbuse | null => {
  if (input == null) return null;

  const run = (Array.isArray(input) ? pickLatestCollectionRun(input as AnyRecord[]) : input) as AnyRecord;
  if (!run || typeof run !== 'object') return null;

  const ml = run.ML_ABUSE_DETECTION ?? run.ML_Abuse_Detection;
  const llm = run.LLM_ABUSE_ANALYSIS ?? run.llm_abuse_analysis ?? run.LLM_Abuse_Analysis;

  if (ml == null && llm == null && run.Overall_Abuse_Score == null) {
    return null;
  }

  return {
    ML_ABUSE_DETECTION: Array.isArray(ml) ? ml : undefined,
    Overall_Abuse_Score: run.Overall_Abuse_Score,
    LLM_ABUSE_ANALYSIS: llm as NormalizedCustomAbuse['LLM_ABUSE_ANALYSIS']
  };
};

const normalizeTimestamp = (timestamp: any): string => {
  if (!timestamp) return '';
  if (typeof timestamp === 'object' && timestamp.$date) return String(timestamp.$date);
  if (typeof timestamp === 'string') return timestamp;
  return '';
};

const createVersionId = (item: AnyRecord, fallbackIndex: number): string => {
  return item.result_id || (item.title && `${item.title}-${String(item.timestamp ?? '')}`) || `abuse-${fallbackIndex}`;
};

const getArray = (value: unknown): AnyRecord[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is AnyRecord => Boolean(item) && typeof item === 'object');
  }
  if (value && typeof value === 'object') {
    return [value as AnyRecord];
  }
  return [];
};

const dedupeByResultId = (items: AnyRecord[]): AnyRecord[] => {
  const seen = new Set<string>();
  const result: AnyRecord[] = [];

  items.forEach((item, index) => {
    const id = createVersionId(item, index);
    if (!seen.has(id)) {
      seen.add(id);
      result.push({ ...item, result_id: item.result_id || id });
    }
  });

  return result;
};

const sortByTimestampAsc = (items: AnyRecord[]): AnyRecord[] => {
  return [...items].sort((a, b) => {
    const tsA = normalizeTimestamp(a.timestamp);
    const tsB = normalizeTimestamp(b.timestamp);
    return tsA.localeCompare(tsB);
  });
};

const sortByTimestampDesc = (items: AnyRecord[]): AnyRecord[] => {
  return [...items].sort((a, b) => {
    const tsA = normalizeTimestamp(a.timestamp);
    const tsB = normalizeTimestamp(b.timestamp);
    return tsB.localeCompare(tsA);
  });
};

const collectAbuseRuns = (projectResults: AnyRecord | null | undefined): AnyRecord[] => {
  if (!projectResults) return [];

  const pickSafety = (root: AnyRecord | undefined) => {
    if (!root) return [];
    const primary = getArray(root.ai_safety);
    if (primary.length) return primary;
    return getArray(root.abuse_ai);
  };

  const combined = [
    ...pickSafety(projectResults?.results),
    ...pickSafety(projectResults?.collection_results),
    ...pickSafety(projectResults?.feature_results),
    ...getArray(projectResults?.ai_safety),
    ...getArray(projectResults?.abuse_ai)
  ].filter(isWorkbenchNonRerunRun);

  return dedupeByResultId(combined);
};

const selectRun = (runs: AnyRecord[], selectedResultId?: string): AnyRecord | null => {
  if (!runs.length) return null;

  if (selectedResultId) {
    const matched = runs.find(run => run.result_id === selectedResultId);
    if (matched) return matched;
  }

  return sortByTimestampDesc(runs)[0] ?? runs[0] ?? null;
};

const getAnalysisFromItem = (item: AnyRecord | null | undefined): LLMAbuseAnalysis | null => {
  if (!item) return null;

  return (item.LLM_ABUSE_ANALYSIS ||
    item.llm_abuse_analysis ||
    item.LLM_Abuse_Analysis ||
    null) as LLMAbuseAnalysis | null;
};

const getAnalysisFromSource = (source: unknown, selectedResultId?: string): LLMAbuseAnalysis | null => {
  const items = getArray(source);
  if (!items.length) return null;

  const chosen = selectRun(items, selectedResultId);
  return getAnalysisFromItem(chosen);
};

const getTopLevelAnalysis = (projectResults: AnyRecord | null | undefined): LLMAbuseAnalysis | null => {
  if (!projectResults) return null;

  return (projectResults.LLM_ABUSE_ANALYSIS ||
    projectResults.llm_abuse_analysis ||
    projectResults.LLM_Abuse_Analysis ||
    null) as LLMAbuseAnalysis | null;
};

const getSafetyAnalysisFromRoot = (root: AnyRecord | undefined, selectedResultId?: string): LLMAbuseAnalysis | null =>
  getAnalysisFromSource(root?.ai_safety, selectedResultId) || getAnalysisFromSource(root?.abuse_ai, selectedResultId);

const getAnalysisFromProjectResults = (
  projectResults: AnyRecord | null | undefined,
  selectedResultId?: string
): LLMAbuseAnalysis | null => {
  if (!projectResults) return null;

  const runs = collectAbuseRuns(projectResults);
  const selectedRun = selectRun(runs, selectedResultId);

  return (
    getAnalysisFromItem(selectedRun) ||
    getSafetyAnalysisFromRoot(projectResults?.results, selectedResultId) ||
    getSafetyAnalysisFromRoot(projectResults?.collection_results, selectedResultId) ||
    getSafetyAnalysisFromRoot(projectResults?.feature_results, selectedResultId) ||
    getTopLevelAnalysis(projectResults)
  );
};

const getProjectId = (projectResults: AnyRecord | null | undefined): string | null => {
  return (
    projectResults?.projectId ||
    projectResults?.results?.projectId ||
    projectResults?.project_id ||
    projectResults?.results?.project_id ||
    localStorage.getItem('projectId') ||
    localStorage.getItem('selectedProjectId') ||
    window.location.search.match(/projectId=([^&]+)/)?.[1] ||
    null
  );
};

const getMlCategoryItems = (run: AnyRecord | null): MLAbuseCategoryItem[] => {
  if (!run) return [];
  if (Array.isArray(run.ML_ABUSE_DETECTION)) return run.ML_ABUSE_DETECTION;
  if (Array.isArray(run.ML_Abuse_Detection)) return run.ML_Abuse_Detection;
  return [];
};

const toMlAbuseDetectionData = (run: AnyRecord): MLAbuseDetectionData | null => {
  const mlCategories = getMlCategoryItems(run);
  if (!mlCategories.length) return null;

  return {
    result_id: run.result_id || '',
    title: run.title || '',
    timestamp: normalizeTimestamp(run.timestamp),
    domain: run.domain || '',
    userId: run.userId || '',
    projectId: run.projectId || '',
    environment: run.environment || '',
    ML_Abuse_Detection: mlCategories,
    Overall_Abuse_Score: Number(run.Overall_Abuse_Score || 0)
  };
};

const buildLlmAnalysisDerivedData = (
  analysis: LLMAbuseAnalysis | null
): Omit<AbuseDerivedData, 'mlAbuseDetectionRaw'> => {
  if (!analysis) {
    return {
      llmData: [],
      overallScore: 0,
      driftData: []
    };
  }

  const extractedScore =
    analysis.Summary?.Overall_Abuse_Index ??
    analysis.Summary?.overall_abuse_index ??
    analysis.Summary?.OverallAbuseIndex ??
    analysis.Overall_Abuse_Index ??
    analysis.overall_abuse_index ??
    analysis.OverallAbuseIndex ??
    0;

  const overallScore = Number(extractedScore ?? 0);

  let driftData: Array<{ eventIndex: number; abuseScore: number }> = [];

  if (Array.isArray(analysis.Drift) && analysis.Drift.length > 0) {
    driftData = analysis.Drift.map(item => ({
      eventIndex: Number(item.Event_Index),
      abuseScore: Number(item.Abuse_Score_Percent || 0)
    })).sort((a, b) => a.eventIndex - b.eventIndex);
  } else if (Array.isArray(analysis.Findings) && analysis.Findings.length > 0) {
    const eventMap = new Map<number, number[]>();

    analysis.Findings.forEach(finding => {
      const eventIndex = Number(finding.Event_Index);
      const severityScore = Number(finding.Severity_Score || 0) * 100;

      if (!eventMap.has(eventIndex)) {
        eventMap.set(eventIndex, []);
      }

      eventMap.get(eventIndex)!.push(severityScore);
    });

    driftData = Array.from(eventMap.entries())
      .map(([eventIndex, scores]) => ({
        eventIndex,
        abuseScore: scores.reduce((sum, score) => sum + score, 0) / scores.length
      }))
      .sort((a, b) => a.eventIndex - b.eventIndex);
  }

  const llmData: AbuseDataItem[] = Array.isArray(analysis.Findings)
    ? analysis.Findings.map(finding => ({
        Action: finding.Action || '',
        Category: finding.Category || '',
        'Severity Score': Number(finding.Severity_Score || 0),
        Risk: finding.Severity_Level || '',
        Observation: finding.Rationale || ''
      }))
    : [];

  return {
    llmData,
    overallScore,
    driftData
  };
};

const buildCustomDerivedData = (customData: NormalizedCustomAbuse): AbuseDerivedData => {
  const severityData: AbuseDataItem[] = [];
  const driftData: Array<{ eventIndex: number; abuseScore: number }> = [];
  let overallScore = Number(customData.Overall_Abuse_Score ?? 0);
  const mlCategories = customData.ML_ABUSE_DETECTION ?? (customData as AnyRecord).ML_Abuse_Detection;

  if (Array.isArray(customData.LLM_ABUSE_ANALYSIS)) {
    customData.LLM_ABUSE_ANALYSIS.forEach((item: any, index) => {
      const severityScore = Number(item?.['Severity Score'] ?? item?.SeverityScore ?? 0);

      severityData.push({
        Action: item?.Action || '',
        Category: item?.Category || '',
        'Severity Score': severityScore,
        Risk: item?.Risk || '',
        Observation: item?.Observation || ''
      });

      driftData.push({
        eventIndex: index + 1,
        abuseScore: severityScore * 100
      });
    });
  } else if (customData.LLM_ABUSE_ANALYSIS && typeof customData.LLM_ABUSE_ANALYSIS === 'object') {
    const nestedAnalysis = customData.LLM_ABUSE_ANALYSIS as any;
    overallScore = Number(nestedAnalysis?.Summary?.Overall_Abuse_Index ?? overallScore);

    if (Array.isArray(nestedAnalysis?.Findings)) {
      nestedAnalysis.Findings.forEach((finding: any) => {
        severityData.push({
          Action: finding?.Action || '',
          Category: finding?.Category || '',
          'Severity Score': Number(finding?.Severity_Score ?? 0),
          Risk: finding?.Severity_Level || '',
          Observation: finding?.Rationale || ''
        });
      });
    }
  }

  return {
    llmData: severityData,
    mlAbuseDetectionRaw: Array.isArray(mlCategories)
      ? [
          {
            result_id: 'custom-run',
            title: 'Custom Run',
            timestamp: '',
            domain: '',
            userId: '',
            projectId: '',
            environment: '',
            ML_Abuse_Detection: mlCategories,
            Overall_Abuse_Score: Number(customData.Overall_Abuse_Score || 0)
          }
        ]
      : [],
    overallScore,
    driftData
  };
};

const buildHistoricalDrift = (runs: AnyRecord[]): Array<{ eventIndex: number; abuseScore: number }> => {
  const sortedRuns = sortByTimestampAsc(runs);

  return sortedRuns.reduce<Array<{ eventIndex: number; abuseScore: number }>>((acc, run, index) => {
    if (run.Overall_Abuse_Score !== undefined && run.Overall_Abuse_Score !== null) {
      acc.push({
        eventIndex: index + 1,
        abuseScore: Number(run.Overall_Abuse_Score) * 100
      });
    }
    return acc;
  }, []);
};

const buildDerivedDataFromProjectResults = (
  projectResults: AnyRecord | null | undefined,
  selectedResultId: string | undefined,
  llmAbuseAnalysis: LLMAbuseAnalysis | null
): AbuseDerivedData => {
  if (!projectResults) return emptyDerivedData;

  const allRuns = collectAbuseRuns(projectResults);
  const selectedRun = selectRun(allRuns, selectedResultId);

  const selectedRunMl = selectedRun ? toMlAbuseDetectionData(selectedRun) : null;

  const allMlRuns = dedupeByResultId(allRuns)
    .map(toMlAbuseDetectionData)
    .filter((item): item is MLAbuseDetectionData => Boolean(item));

  const mlAbuseDetectionRaw = selectedRunMl && selectedResultId ? [selectedRunMl] : allMlRuns;

  const fallbackAnalysis = getAnalysisFromProjectResults(projectResults, selectedResultId);
  const analysisToUse = llmAbuseAnalysis || fallbackAnalysis;

  const llmDerived = buildLlmAnalysisDerivedData(analysisToUse);
  const historicalDriftData = buildHistoricalDrift(allRuns);

  return {
    llmData: llmDerived.llmData,
    mlAbuseDetectionRaw,
    overallScore: analysisToUse
      ? llmDerived.overallScore
      : Number(mlAbuseDetectionRaw[0]?.Overall_Abuse_Score ?? 0) * 100,
    driftData: llmDerived.driftData.length > 1 ? llmDerived.driftData : historicalDriftData
  };
};

export const AbuseCard = forwardRef<AbuseCardRef, AbuseCardProps>(
  ({ customData, showInlineVersionControls = true, lane, onVersionsReady }, ref) => {
    const dispatch = useDispatch();
    const projectResults = useSelector(selectProjectResults);
    const selectedVersion = useSelector(selectSelectedVersion);

    const [historyOpen, setHistoryOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      handleHistoryOpen: () => setHistoryOpen(true)
    }));
    const normalizedCustomData = useMemo(() => normalizeCustomAbuseInput(customData), [customData]);

    const [isLoading, setIsLoading] = useState<boolean>(!normalizedCustomData);
    const [llmAbuseAnalysis, setLlmAbuseAnalysis] = useState<LLMAbuseAnalysis | null>(() => {
      const llm = normalizedCustomData?.LLM_ABUSE_ANALYSIS;
      return llm && !Array.isArray(llm) && typeof llm === 'object' ? (llm as LLMAbuseAnalysis) : null;
    });

    const selectedResultId = selectedVersion?.result_id;

    const abuseVersions = useMemo(() => collectAbuseRuns(projectResults), [projectResults]);

    useEffect(() => {
      if (normalizedCustomData) {
        onVersionsReady?.({ total: 0, runsWithMl: 0, runsWithLlm: 0 });
        return;
      }
      const runsWithMl = abuseVersions.filter(v => getMlCategoryItems(v).length > 0).length;
      const runsWithLlm = abuseVersions.filter(v => getAnalysisFromItem(v) != null).length;
      onVersionsReady?.({ total: abuseVersions.length, runsWithMl, runsWithLlm });
    }, [abuseVersions, normalizedCustomData, onVersionsReady]);

    const versionItems: VersionItem[] = useMemo(() => {
      return abuseVersions.map((version, index) => {
        const timestamp = normalizeTimestamp(version.timestamp) || 'Custom Run';

        return {
          id: version.result_id ?? `abuse-${index}`,
          timestamp,
          date: timestamp,
          modifier: version.userId || 'System',
          isCurrent: selectedResultId === version.result_id,
          title: version.title || `Abuse AI Run ${index + 1}`
        };
      });
    }, [abuseVersions, selectedResultId]);

    const derivedData = useMemo<AbuseDerivedData>(() => {
      if (normalizedCustomData) {
        const fromCustom = buildCustomDerivedData(normalizedCustomData);
        if (hasDerivedAbuseData(fromCustom)) {
          return fromCustom;
        }
      }

      return buildDerivedDataFromProjectResults(projectResults, selectedResultId, llmAbuseAnalysis);
    }, [normalizedCustomData, projectResults, selectedResultId, llmAbuseAnalysis]);

    useEffect(() => {
      if (normalizedCustomData) {
        setIsLoading(false);
        return;
      }

      const analysisInStore = getAnalysisFromProjectResults(projectResults, selectedResultId);
      if (analysisInStore) {
        setLlmAbuseAnalysis(analysisInStore);
        setIsLoading(false);
        return;
      }

      const projectId = getProjectId(projectResults);
      if (!projectId) {
        setIsLoading(false);
        return;
      }

      let isMounted = true;

      const fetchProjectData = async () => {
        try {
          const response = await api.get(`/project/get-projects-by-id?projectId=${projectId}`);
          const responseData = response.data;
          const projectData = response.data?.project || response.data;

          const remoteAnalysis =
            getSafetyAnalysisFromRoot(responseData?.results, selectedResultId) ||
            getSafetyAnalysisFromRoot(responseData?.collection_results, selectedResultId) ||
            getSafetyAnalysisFromRoot(responseData?.feature_results, selectedResultId) ||
            getSafetyAnalysisFromRoot(projectData?.results, selectedResultId) ||
            getSafetyAnalysisFromRoot(projectData?.collection_results, selectedResultId) ||
            getSafetyAnalysisFromRoot(projectData?.feature_results, selectedResultId) ||
            getTopLevelAnalysis(responseData) ||
            getTopLevelAnalysis(projectData);

          if (isMounted && remoteAnalysis) {
            setLlmAbuseAnalysis(remoteAnalysis);
          }
        } catch (error) {
          console.error('AbuseCard - Error fetching project data:', error);
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      };

      fetchProjectData();

      return () => {
        isMounted = false;
      };
    }, [normalizedCustomData, projectResults, selectedResultId]);

    const handleVersionHistorySelect = (item: VersionItem) => {
      const raw = abuseVersions.find(version => version.result_id === item.id);
      if (raw) {
        dispatch(setSelectedVersion(raw));
      }
      setHistoryOpen(false);
    };

    const hasAnyData = hasDerivedAbuseData(derivedData);

    if (isLoading && !projectResults && !normalizedCustomData) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <CircularProgress />
        </div>
      );
    }

    if (!hasAnyData) {
      return (
        <div className="text-center py-5">
          <p className="text-muted">No abuse detection data available. Please run the analysis first.</p>
        </div>
      );
    }

    const showMlCharts = lane !== 'llm';
    const showLlmCharts = lane !== 'ml';

    return (
      <div>
        {showInlineVersionControls && !normalizedCustomData && abuseVersions.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip
              label={`${abuseVersions.length} version${abuseVersions.length !== 1 ? 's' : ''}`}
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 500,
                borderColor: 'primary.main',
                color: 'primary.main'
              }}
            />
            <Button
              onClick={() => setHistoryOpen(true)}
              size="small"
              variant="contained"
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                px: 2,
                py: 0.5,
                borderRadius: 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }
              }}
            >
              View All
            </Button>
          </Box>
        )}

        <Row className="g-3 mt-1">
          {showLlmCharts && derivedData.overallScore > 0 && (
            <Col xs={12}>
              <AbuseIndexChart
                overallScore={derivedData.overallScore}
                title={`Overall Abuse Index: ${derivedData.overallScore.toFixed(1)} / 100`}
                description="Overall abuse detection score across all categories"
              />
            </Col>
          )}

          {showMlCharts && derivedData.mlAbuseDetectionRaw.length > 0 && (
            <Col xs={12}>
              <MLAbuseDetectionChart
                data={derivedData.mlAbuseDetectionRaw}
                title="ML Abuse Detection by Category"
                description="Mean severity percentage for each abuse category detected in ML models, grouped by detection results"
              />
            </Col>
          )}

          {showLlmCharts && derivedData.llmData.length > 0 && (
            <Col xs={12}>
              <AbuseSeverityChart
                data={derivedData.llmData}
                title="Abuse Severity by Category"
                description="Mean severity percentage for each abuse category detected in LLM outputs"
              />
            </Col>
          )}

          {showLlmCharts && derivedData.driftData.length > 0 && (
            <Col xs={12}>
              <AbuseDriftChart
                data={derivedData.driftData}
                title="Abuse Drift Across Events"
                description="Abuse score percentage variation across different events"
              />
            </Col>
          )}
        </Row>

        <VersionHistory
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          versions={versionItems}
          onVersionSelect={handleVersionHistorySelect}
          title="Abuse AI — All versions"
        />
      </div>
    );
  }
);

AbuseCard.displayName = 'AbuseCard';
